import type {
  MotionPropsFor,
  RadioGroupProps,
  SimpleGridProps,
  SystemProps,
} from '@hover/blueprint';
import { Alert, Link, RadioGroup, forwardRef, motion } from '@hover/blueprint';
import type { SkeletonProps } from '@hover/blueprint/chakra';
import React from 'react';
import { Trans } from 'react-i18next';

import type { CustomizationCategory } from '../../constants/customizationCategories';
import { productBrightnessWithDefault } from '../../helpers/color';
import { useHomeownerTranslate } from '../../hooks';
import { useIsProductCatalogPromptsEnabled } from '../../hooks/split';
import type {
  Aspect,
  DesignerMaterialProduct,
  DesignerPaintProduct,
  DesignerProduct,
} from '../../types';
import { isMaterialProduct, isPaintProduct } from '../../types';
import { usePalettes } from '../Customize/usePalettes';

import { PaletteColor } from './PaletteColor';
import { PaletteMaterial } from './PaletteMaterial';
import type { PaletteTileProps } from './PaletteTile';
import { useMaterials, usePaintColors, usePaletteSelection } from './hooks';

interface BasePaletteProps
  extends Omit<RadioGroupProps, 'children'>,
    Omit<SimpleGridProps, keyof RadioGroupProps> {
  aspect?: Aspect;
  category: CustomizationCategory;
  disableRefresh?: boolean;
  isLoading?: boolean;
  isRefreshable?: boolean;
  itemProps?: Partial<MotionPropsFor<SystemProps>> &
    Pick<SkeletonProps, 'colorScheme'>;
  onSelectPaletteClick?: () => void;
  selectedPalette: string;
  include3dFusionMaterials?: boolean;
}

interface MaterialPaletteProps extends BasePaletteProps {
  type: 'material';
  selectedItem?: DesignerMaterialProduct | null;
  onSelectItem?: (color: DesignerMaterialProduct | null) => void;
  onReselectItem?: (color: DesignerMaterialProduct | null) => void;
}

interface ColorPaletteProps extends BasePaletteProps {
  type: 'color';
  selectedItem?: DesignerPaintProduct | null;
  onSelectItem?: (color: DesignerPaintProduct | null) => void;
  onReselectItem?: (color: DesignerPaintProduct | null) => void;
}

const MotionPaletteMaterial = motion(PaletteMaterial);
const MotionPaletteColor = motion(PaletteColor);

export type PaletteProps = MaterialPaletteProps | ColorPaletteProps;

const Palette = forwardRef<PaletteProps, 'div'>(
  (
    {
      aspect = 'exterior',
      category,
      disableRefresh,
      isRefreshable,
      itemProps: itemPropsProp,
      include3dFusionMaterials,
      onSelectItem,
      onReselectItem,
      selectedItem,
      selectedPalette,
      type,
      ...props
    },
    ref,
  ) => {
    const t = useHomeownerTranslate();
    const { onSelectPaletteOpen } = usePalettes();

    const isProductCatalogPromptsEnabled = useIsProductCatalogPromptsEnabled();

    const [allColors, { loading: isLoadingPaintColors }] = usePaintColors({
      aspect,
      category,
    });
    const [allMaterials, { loading: isLoadingMaterials }] = useMaterials({
      aspect,
      category,
      include3dFusionMaterials,
    });

    const paletteColors = usePaletteSelection(allColors, selectedPalette);
    const paletteMaterials = usePaletteSelection(allMaterials, selectedPalette);

    const isMaterial = type === 'material';

    const isLoading = isMaterial ? isLoadingMaterials : isLoadingPaintColors;
    const products = isMaterial ? paletteMaterials : paletteColors;

    const items =
      products.length === 0 && isLoading
        ? Array.from<{ isLoading: true }>({ length: 30 }).fill({
            isLoading: true,
          })
        : products.slice().sort(
            // Some materials still have `hex` associated, but some just have
            // empty strings, so we can still sort them by brightness but we
            // need to default the empty entries to 0
            (a, b) =>
              productBrightnessWithDefault(b) - productBrightnessWithDefault(a),
          );

    const handleReselect = (product: DesignerProduct) => {
      if (
        disableRefresh ||
        !isRefreshable ||
        !selectedItem ||
        !isMaterialProduct(product) ||
        type !== 'material' ||
        selectedItem.id !== product.id
      ) {
        return;
      }

      onReselectItem?.(product);
    };

    if (!isLoading && items.length === 0) {
      return (
        <Alert status="error">
          <Trans
            components={{
              PaletteName: <strong />,
              SelectPalette: (
                <Link
                  as="button"
                  colorScheme="neutral"
                  cursor="pointer"
                  fontWeight="bodySemiBold"
                  onClick={onSelectPaletteOpen}
                  padding={0}
                  textDecoration="underline"
                />
              ),
            }}
            i18nKey={`palette.noneFound.${type}`}
            t={t}
            values={{ palette: selectedPalette }}
          />
        </Alert>
      );
    }

    return (
      <RadioGroup
        display={{ base: 'flex', tablet: 'grid' }}
        flexDirection="row"
        gap={{ base: '200', tablet: '500' }}
        gridTemplateColumns={{ base: 'repeat(auto-fill, minmax(125px, 1fr))' }}
        onChange={value => {
          const configurationId = value ? parseInt(value, 10) : null;
          const productCatalogId = value;

          const id = isProductCatalogPromptsEnabled
            ? productCatalogId
            : configurationId;

          if (type === 'material') {
            onSelectItem?.(
              allMaterials.find(({ id: materialId }) => materialId === id) ??
                null,
            );
          } else {
            onSelectItem?.(
              allColors.find(({ id: colorId }) => colorId === id) ?? null,
            );
          }
        }}
        pb={{ base: 0, tablet: '200' }}
        pt="200"
        sx={{
          '@media (min-width: 2000px)': {
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          },
        }}
        value={selectedItem?.id.toString() ?? ''}
        width={{ tablet: '100%' }}
        {...props}
        ref={ref}
      >
        {items.map((product, index) => {
          const isLoadingItem = 'isLoading' in product;

          const key = isLoadingItem
            ? index.toString()
            : `${selectedPalette}-${product.id}`;

          const itemProps = {
            // For some reason, Chakra's default UUID (via `useId`) behavior for
            // the `value` and `id` of the internal `useRadio` inputs causes
            // hydration mismatches for these attributes on the input.
            // Fortunately, we have a unique identifier we can use instead.
            inputProps: { id: key, name: key },
            isLoading: isLoadingItem,
            layout: true,
            key,
            width: { base: '600', tablet: 'auto' },
            ...itemPropsProp,
          } satisfies MotionPropsFor<Partial<PaletteTileProps>>;

          return type === 'color' ? (
            <MotionPaletteColor
              paintColor={
                !isLoadingItem && isPaintProduct(product) ? product : undefined
              }
              {...itemProps}
            />
          ) : (
            <MotionPaletteMaterial
              disableRefresh={disableRefresh}
              isRefreshable={isRefreshable}
              material={
                !isLoadingItem && isMaterialProduct(product)
                  ? product
                  : undefined
              }
              onClick={
                isLoadingItem ? undefined : () => handleReselect(product)
              }
              {...itemProps}
            />
          );
        })}
      </RadioGroup>
    );
  },
);

export { Palette };
